import React from 'react';
import {
  DocumentTextIcon,
  UserIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  HandRaisedIcon,
  XMarkIcon,
  ClockIcon,
  EnvelopeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

export const Terms: React.FC = () => {
  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: HandRaisedIcon,
      content: [
        {
          subtitle: 'Agreement to Terms',
          text: 'By accessing or using SynqTag\'s services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using our services.'
        },
        {
          subtitle: 'Legal Capacity',
          text: 'You represent that you are at least 18 years old and have the legal capacity to enter into this agreement. If you are using our services on behalf of an organization, you represent that you have the authority to bind that organization to these terms.'
        },
        {
          subtitle: 'Modifications',
          text: 'We reserve the right to modify these terms at any time. We will notify users of material changes via email or through our platform. Continued use of our services after changes constitutes acceptance of the new terms.'
        }
      ]
    },
    {
      id: 'service-description',
      title: 'Description of Service',
      icon: DocumentTextIcon,
      content: [
        {
          subtitle: 'Platform Overview',
          text: 'SynqTag is a cloud-based workspace management platform that enables teams to organize projects, collaborate on tasks, manage documents, and streamline workflows through an intuitive web interface.'
        },
        {
          subtitle: 'Service Availability',
          text: 'We strive to maintain 99.9% uptime but do not guarantee uninterrupted access to our services. We may temporarily suspend services for maintenance, updates, or technical issues with advance notice when possible.'
        },
        {
          subtitle: 'Feature Updates',
          text: 'We regularly update and improve our services. New features may be added, and existing features may be modified or discontinued with reasonable notice to users.'
        }
      ]
    },
    {
      id: 'user-accounts',
      title: 'User Accounts and Responsibilities',
      icon: UserIcon,
      content: [
        {
          subtitle: 'Account Creation',
          text: 'To use our services, you must create an account with accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials.'
        },
        {
          subtitle: 'Account Security',
          text: 'You are solely responsible for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other breach of security.'
        },
        {
          subtitle: 'Account Termination',
          text: 'You may terminate your account at any time through your account settings. We may suspend or terminate accounts that violate these terms or for other legitimate business reasons.'
        },
        {
          subtitle: 'Data Responsibility',
          text: 'You are responsible for all content and data you upload, store, or transmit through our services. You must ensure you have the right to use and share all content you provide.'
        }
      ]
    },
    {
      id: 'acceptable-use',
      title: 'Acceptable Use Policy',
      icon: ShieldCheckIcon,
      content: [
        {
          subtitle: 'Permitted Uses',
          text: 'Our services may only be used for lawful business and personal purposes in accordance with these terms and applicable laws and regulations.'
        },
        {
          subtitle: 'Prohibited Activities',
          text: 'You may not: (a) violate any laws or regulations; (b) infringe on intellectual property rights; (c) transmit harmful or malicious code; (d) attempt to gain unauthorized access to our systems; (e) use our services for spam or unsolicited communications; (f) engage in any activity that could damage, disable, or impair our services.'
        },
        {
          subtitle: 'Content Standards',
          text: 'All content must comply with our community standards. You may not upload content that is illegal, harmful, threatening, abusive, defamatory, obscene, or otherwise objectionable.'
        },
        {
          subtitle: 'Compliance Monitoring',
          text: 'We reserve the right to monitor use of our services for compliance with these terms and may take appropriate action against violations, including account suspension or termination.'
        }
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: LockClosedIcon,
      content: [
        {
          subtitle: 'Our Rights',
          text: 'All rights, title, and interest in SynqTag, including our software, technology, trademarks, and copyrights, remain our exclusive property. These terms do not grant you any rights to our intellectual property except as necessary to use our services.'
        },
        {
          subtitle: 'Your Content',
          text: 'You retain ownership of your content and data. By using our services, you grant us a limited license to use, store, and process your content solely to provide our services to you.'
        },
        {
          subtitle: 'Feedback',
          text: 'Any feedback, suggestions, or ideas you provide about our services become our property and may be used to improve our platform without compensation to you.'
        },
        {
          subtitle: 'DMCA Compliance',
          text: 'We respect intellectual property rights and comply with the Digital Millennium Copyright Act. If you believe your copyright has been infringed, please contact us with the required information.'
        }
      ]
    },
    {
      id: 'payment-terms',
      title: 'Payment Terms',
      icon: CurrencyDollarIcon,
      content: [
        {
          subtitle: 'Subscription Plans',
          text: 'Our services are offered through various subscription plans with different features and pricing. Current pricing is available on our website and may change with 30 days notice.'
        },
        {
          subtitle: 'Payment Processing',
          text: 'Payments are processed through secure third-party payment processors. You authorize us to charge your chosen payment method for all applicable fees.'
        },
        {
          subtitle: 'Billing Cycles',
          text: 'Subscription fees are billed in advance on a monthly or annual basis, depending on your chosen plan. Billing occurs on the same date each billing period.'
        },
        {
          subtitle: 'Refunds and Cancellations',
          text: 'You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. We do not provide refunds for partial billing periods except as required by law.'
        },
        {
          subtitle: 'Late Payments',
          text: 'If payment fails, we may suspend your account after reasonable notice. You remain responsible for all outstanding fees plus any collection costs.'
        }
      ]
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of Liability',
      icon: ExclamationTriangleIcon,
      content: [
        {
          subtitle: 'Service Disclaimers',
          text: 'Our services are provided "as is" without warranties of any kind. We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.'
        },
        {
          subtitle: 'Limitation of Damages',
          text: 'To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, data loss, or business interruption.'
        },
        {
          subtitle: 'Liability Cap',
          text: 'Our total liability to you for all claims arising from or relating to these terms or our services shall not exceed the amount you paid us in the 12 months preceding the claim.'
        },
        {
          subtitle: 'Essential Purpose',
          text: 'The limitations in this section apply even if any limited remedy fails of its essential purpose. Some jurisdictions do not allow certain limitations, so these may not apply to you.'
        }
      ]
    },
    {
      id: 'disclaimers',
      title: 'Disclaimers',
      icon: ExclamationTriangleIcon,
      content: [
        {
          subtitle: 'Service Availability',
          text: 'We do not guarantee that our services will be available at all times or free from errors, viruses, or other harmful components. You use our services at your own risk.'
        },
        {
          subtitle: 'Third-Party Services',
          text: 'Our platform may integrate with third-party services. We are not responsible for the availability, accuracy, or content of third-party services and do not endorse them.'
        },
        {
          subtitle: 'Data Backup',
          text: 'While we implement backup procedures, you are responsible for maintaining your own backups of important data. We are not liable for any data loss.'
        }
      ]
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: ShieldCheckIcon,
      content: [
        {
          subtitle: 'Your Indemnification Obligations',
          text: 'You agree to indemnify, defend, and hold harmless SynqTag and its officers, directors, employees, and agents from any claims, damages, costs, and expenses arising from: (a) your use of our services; (b) your violation of these terms; (c) your violation of any rights of another party; (d) your content or data.'
        },
        {
          subtitle: 'Process',
          text: 'We will notify you of any claims subject to indemnification and may assume control of the defense. You may not settle any claim without our prior written consent.'
        }
      ]
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: XMarkIcon,
      content: [
        {
          subtitle: 'Termination by You',
          text: 'You may terminate your account at any time by following the cancellation process in your account settings. Termination does not relieve you of obligations to pay any outstanding fees.'
        },
        {
          subtitle: 'Termination by Us',
          text: 'We may terminate or suspend your account immediately if you violate these terms, fail to pay fees, or for other legitimate business reasons. We will provide reasonable notice when possible.'
        },
        {
          subtitle: 'Effect of Termination',
          text: 'Upon termination, your right to use our services ceases immediately. We may delete your account and data after a reasonable grace period. Provisions of these terms that should survive termination will remain in effect.'
        },
        {
          subtitle: 'Data Retrieval',
          text: 'You may request a copy of your data within 30 days of termination, subject to our data export capabilities and applicable laws.'
        }
      ]
    },
    {
      id: 'governing-law',
      title: 'Governing Law and Dispute Resolution',
      icon: ScaleIcon,
      content: [
        {
          subtitle: 'Governing Law',
          text: 'These terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles. Any disputes will be resolved in the courts of [Jurisdiction].'
        },
        {
          subtitle: 'Dispute Resolution',
          text: 'Before filing any legal action, you agree to attempt to resolve disputes through good faith negotiations. If unsuccessful, disputes may be resolved through binding arbitration.'
        },
        {
          subtitle: 'Class Action Waiver',
          text: 'You agree to resolve disputes individually and waive any right to participate in class action lawsuits or class-wide arbitrations.'
        },
        {
          subtitle: 'Injunctive Relief',
          text: 'Nothing in this section prevents either party from seeking injunctive relief for violations of intellectual property rights or confidentiality obligations.'
        }
      ]
    },
    {
      id: 'general-provisions',
      title: 'General Provisions',
      icon: DocumentTextIcon,
      content: [
        {
          subtitle: 'Entire Agreement',
          text: 'These terms constitute the entire agreement between you and SynqTag regarding our services and supersede all prior agreements and understandings.'
        },
        {
          subtitle: 'Severability',
          text: 'If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.'
        },
        {
          subtitle: 'No Waiver',
          text: 'Our failure to enforce any provision of these terms does not constitute a waiver of that provision or any other provision.'
        },
        {
          subtitle: 'Assignment',
          text: 'You may not assign these terms without our written consent. We may assign these terms to any affiliate or in connection with a merger, acquisition, or sale of assets.'
        },
        {
          subtitle: 'Force Majeure',
          text: 'We are not liable for any failure to perform due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, or government actions.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-width section-padding text-center">
          <div
          >
            <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <DocumentTextIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold mb-6">
              <span className="gradient-text">Terms of Service</span>
            </h1>
            <p className="text-xl text-ink-soft mb-8 max-w-3xl mx-auto">
              These terms govern your use of SynqTag's services. Please read them carefully 
              as they contain important information about your rights and obligations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-ink-soft">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4" />
                <span>Effective Date: January 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4" />
                <span>Last Updated: January 1, 2025</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-white border-b border-line">
        <div className="container-width section-padding">
          <div
          >
            <h2 className="text-2xl font-bold text-ink mb-6">Table of Contents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-3 p-4 rounded-lg border border-line hover:border-primary-300 hover:bg-primary-50 transition-all group"
                  >
                    <Icon className="h-5 w-5 text-ink-soft group-hover:text-primary-600" />
                    <span className="text-ink group-hover:text-primary-700 font-medium">
                      {section.title}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 bg-paper-deep">
        <div className="container-width section-padding">
          <div className="max-w-4xl mx-auto space-y-16">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-ink">
                      {section.title}
                    </h2>
                  </div>
                  
                  <div className="space-y-6">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <h3 className="text-xl font-semibold text-ink mb-3">
                          {item.subtitle}
                        </h3>
                        <p className="text-ink-soft leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="container-width section-padding">
          <div
            className="max-w-3xl mx-auto text-center"
          >
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-amber-900 mb-4">Important Notice</h3>
            <p className="text-amber-800 leading-relaxed">
              These terms contain important limitations on our liability and require you to resolve 
              disputes through arbitration. Please read them carefully. If you do not agree to these 
              terms, you must not use our services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container-width section-padding">
          <div
            className="max-w-3xl mx-auto text-center"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <EnvelopeIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-ink mb-6">
              Questions About These Terms?
            </h2>
            <p className="text-xl text-ink-soft mb-8">
              If you have any questions about these terms of service or need clarification 
              on any provisions, please contact us.
            </p>
            <div className="bg-paper-deep rounded-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-ink mb-2">Legal Inquiries</h3>
                  <a href="mailto:team@synqtag.com" className="text-primary-600 hover:text-primary-700">
                    team@synqtag.com
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-ink mb-2">General Support</h3>
                  <a href="mailto:team@synqtag.com" className="text-primary-600 hover:text-primary-700">
                    team@synqtag.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates Section */}
      <section className="py-12 bg-sidebar text-white">
        <div className="container-width section-padding text-center">
          <div
          >
            <h3 className="text-xl font-bold mb-4">Terms Updates</h3>
            <p className="text-primary-200 mb-6 max-w-2xl mx-auto">
              We may update these terms from time to time. Material changes will be communicated 
              via email or through our platform with at least 30 days notice.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-ink-soft">
              <ClockIcon className="h-4 w-4" />
              <span>Continued use after changes constitutes acceptance of new terms.</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};